import { useState, useEffect, createContext, useContext } from 'react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Context for OAuth config
const OAuthConfigContext = createContext(null);

export const useOAuthConfig = () => useContext(OAuthConfigContext);

/**
 * OAuthConfigProvider - Provides OAuth configuration to children
 */
export const OAuthConfigProvider = ({ children, clinicId = null }) => {
  const [oauthConfig, setOauthConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkOAuthConfig = async () => {
      try {
        const url = clinicId 
          ? `${API_URL}/api/oauth-settings/google/config?clinic_id=${clinicId}`
          : `${API_URL}/api/oauth-settings/google/config`;
        
        const response = await axios.get(url);
        setOauthConfig(response.data);
      } catch (error) {
        console.error('Failed to check OAuth config:', error);
        setOauthConfig({ enabled: false });
      } finally {
        setLoading(false);
      }
    };

    checkOAuthConfig();
  }, [clinicId]);

  return (
    <OAuthConfigContext.Provider value={{ oauthConfig, loading }}>
      {children}
    </OAuthConfigContext.Provider>
  );
};

/**
 * GoogleSignInButton - Initiates Google OAuth flow
 * Only shows when custom OAuth is configured
 */
const GoogleSignInButton = ({ 
  clinicId = null,
  className = '',
  variant = 'outline',
  children,
  fallbackToEmergent = false
}) => {
  const [oauthConfig, setOauthConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkOAuthConfig();
  }, [clinicId]);

  const checkOAuthConfig = async () => {
    try {
      const url = clinicId 
        ? `${API_URL}/api/oauth-settings/google/config?clinic_id=${clinicId}`
        : `${API_URL}/api/oauth-settings/google/config`;
      
      const response = await axios.get(url);
      setOauthConfig(response.data);
    } catch (error) {
      console.error('Failed to check OAuth config:', error);
      setOauthConfig({ enabled: false });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    const redirectUrl = `${window.location.origin}/auth/google/callback`;
    
    if (oauthConfig?.enabled && oauthConfig?.client_id) {
      // Use custom Google OAuth
      const googleAuthUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
      googleAuthUrl.searchParams.set('client_id', oauthConfig.client_id);
      googleAuthUrl.searchParams.set('redirect_uri', redirectUrl);
      googleAuthUrl.searchParams.set('response_type', 'code');
      googleAuthUrl.searchParams.set('scope', 'openid email profile');
      googleAuthUrl.searchParams.set('access_type', 'offline');
      googleAuthUrl.searchParams.set('prompt', 'consent');
      
      // Add state parameter for security
      const state = JSON.stringify({ 
        clinic_id: clinicId,
        timestamp: Date.now()
      });
      googleAuthUrl.searchParams.set('state', btoa(state));
      
      window.location.href = googleAuthUrl.toString();
    } else if (fallbackToEmergent) {
      // Fallback to Emergent Auth (disabled by default)
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }
  };

  // Don't render anything if loading or not configured
  if (loading) {
    return null; // Hide while loading
  }

  // Don't render if OAuth not configured and no fallback
  if (!oauthConfig?.enabled && !fallbackToEmergent) {
    return null;
  }

  return (
    <Button
      type="button"
      variant={variant}
      className={`w-full flex items-center justify-center gap-3 ${className}`}
      onClick={handleGoogleSignIn}
    >
      {/* Google Logo SVG */}
      <svg width="18" height="18" viewBox="0 0 24 24">
        <path
          fill="#4285F4"
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        />
        <path
          fill="#34A853"
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        />
        <path
          fill="#FBBC05"
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        />
        <path
          fill="#EA4335"
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        />
      </svg>
      {children || 'Continue with Google'}
    </Button>
  );
};

/**
 * SocialLoginSection - Shows social login options only when available
 */
export const SocialLoginSection = ({ clinicId = null, className = '' }) => {
  const [hasOAuth, setHasOAuth] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkOAuth = async () => {
      try {
        const url = clinicId 
          ? `${API_URL}/api/oauth-settings/google/config?clinic_id=${clinicId}`
          : `${API_URL}/api/oauth-settings/google/config`;
        
        const response = await axios.get(url);
        setHasOAuth(response.data?.enabled || false);
      } catch (error) {
        setHasOAuth(false);
      } finally {
        setLoading(false);
      }
    };

    checkOAuth();
  }, [clinicId]);

  // Don't show anything if loading or no OAuth configured
  if (loading || !hasOAuth) {
    return null;
  }

  return (
    <div className={className}>
      {/* Divider */}
      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-200"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-white text-gray-500">or continue with</span>
        </div>
      </div>
      
      {/* Google Sign-In */}
      <GoogleSignInButton 
        clinicId={clinicId}
        className="border-gray-300 hover:bg-gray-50" 
      />
    </div>
  );
};

export default GoogleSignInButton;
